// Friendly labels and copy for the UAVS page. Kept frontend-only so we can
// iterate on customer-facing wording without backend changes; the backend
// owns the canonical check keys + scoring logic.

export const PILLAR_LABELS = {
  crawlability: 'Crawlability',
  structured_data: 'Structured Data',
  content_depth: 'Content Depth',
  trust_signals: 'Trust Signals',
  ai_presence: 'AI Presence',
};

export const PILLAR_DESCRIPTIONS = {
  crawlability: 'Can AI systems access and read your site?',
  structured_data: 'Does your site tell AI what it is and does?',
  content_depth: 'Is there enough substance for AI to cite you?',
  trust_signals: 'Does AI have reason to recommend you?',
  ai_presence: 'What happens when AI is asked about you?',
};

export const PILLAR_ORDER = [
  'crawlability',
  'structured_data',
  'content_depth',
  'trust_signals',
  'ai_presence',
];

export const CHECK_LABELS = {
  crawl_robots_txt: 'robots.txt AI crawler rules',
  crawl_llms_txt: 'llms.txt presence',
  crawl_ssr: 'Server-side rendering',
  crawl_sitemap: 'Sitemap present and valid',
  crawl_cwv: 'Core Web Vitals',
  schema_local_business: 'LocalBusiness schema',
  schema_organization: 'Organization schema with @id',
  schema_service: 'Service schemas on service pages',
  schema_faqpage: 'FAQPage schema',
  schema_id_graph: 'Connected @id graph',
  content_service_pages: 'Service page depth',
  content_location: 'Genuine local content',
  content_faq: 'FAQ specificity',
  content_headings: 'Heading hierarchy',
  content_speakable: 'Speakable markup',
  trust_nap: 'NAP consistency',
  trust_credentials: 'Credentials and licensing',
  trust_review_markup: 'Reviews with structured markup',
  trust_about_page: 'About page quality',
  trust_blog: 'Topical authority / content history',
  ai_chatgpt: 'ChatGPT presence',
  ai_perplexity: 'Perplexity presence',
  ai_gemini: 'Gemini presence',
  ai_claude: 'Claude presence',
  ai_google_aio: 'Google AI Overview presence',
  ai_brand_accuracy: 'Brand accuracy in AI responses',
  ai_cited_as_source: 'Cited as source in AI answers',
};

export function scoreColor(s, t) {
  if (s >= 70) return t.greenText;
  if (s >= 40) return t.amberText;
  return t.red;
}

export function resultColor(result, t) {
  if (result === 'pass') return t.greenText;
  if (result === 'warn') return t.amberText;
  if (result === 'fail') return t.red;
  return t.grayText;
}

export function resultBg(result, t) {
  if (result === 'pass') return t.greenBg;
  if (result === 'warn') return t.amberBg;
  if (result === 'fail') return t.redBg;
  return t.grayBg;
}

export function resultLabel(result) {
  if (result === 'pass') return 'Passing';
  if (result === 'warn') return 'Needs work';
  if (result === 'fail') return 'Failing';
  // 'error' = our lookup or check failed this scan (not a finding about the
  // business), shown to tenants the same as an untested check.
  if (result === 'skip' || result === 'error') return 'Not tested';
  return result || 'Unknown';
}

// "Critical" picks weighted by both severity (fail > warn) and impact (points
// the check is worth). Used by TopPriorities to surface the 3 highest-leverage
// fixes. Skip pass/skip and any check without recommendation copy — there's
// nothing actionable to show.
export function rankActionableChecks(pillarScores) {
  const items = [];
  for (const [pillarKey, pillar] of Object.entries(pillarScores || {})) {
    if (!pillar?.checkResults) continue;
    for (const check of pillar.checkResults) {
      if (check.result !== 'fail' && check.result !== 'warn') continue;
      if (!check.recommendation) continue;
      const severity = check.result === 'fail' ? 2 : 1;
      const lostPoints = (check.pointsMax || 0) - (check.pointsEarned || 0);
      items.push({ ...check, pillarKey, score: severity * 10 + lostPoints });
    }
  }
  items.sort((a, b) => b.score - a.score);
  return items;
}
